import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { useOpsStore } from "../state/opsStore";
import type { OperatorLive, BatchLive } from "../state/opsStore";

/**
 * Phase 18 WU-18.D5 — Ops Console snapshot seed.
 *
 * On mount, fetches `GET /api/ops/snapshot` + the 3 live lists
 * (operators-live, batch-watchlist, bottlenecks) in parallel and seeds
 * the opsStore so the page paints with real data immediately. The
 * `useOpsLiveUpdates` hook keeps the store fresh from then on.
 *
 * Re-fires snapshot every `pollIntervalMs` (default 15_000) when
 * `reconnecting` is true — the WS drop fallback path. Skipped when the
 * socket is healthy so we don't double-load.
 */

interface SnapshotResponse {
  activeOperators: number;
  currentBatches: number;
  slaStatus: { met: number; approaching: number; breached: number };
  queueDepths: Record<string, number>;
  at: number;
}

interface OperatorsLiveResponse {
  operators: OperatorLive[];
}

interface BatchWatchlistResponse {
  batches: BatchLive[];
}

export function useOpsSnapshot(pollIntervalMs = 15_000) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reconnecting = useOpsStore((s) => s.reconnecting);
  const replaceFromSnapshot = useOpsStore((s) => s.replaceFromSnapshot);

  useEffect(() => {
    let cancelled = false;
    async function fetchSnapshot() {
      try {
        const [snap, live, watch] = await Promise.all([
          api.get<SnapshotResponse>("/ops/snapshot"),
          api.get<OperatorsLiveResponse>("/ops/operators-live"),
          api.get<BatchWatchlistResponse>("/ops/batch-watchlist"),
        ]);
        if (cancelled) return;
        replaceFromSnapshot({
          operators: live.operators,
          batches: watch.batches,
          queueDepths: snap.queueDepths,
        });
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load ops snapshot");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void fetchSnapshot();

    // Polling fallback — only when WS is down.
    let interval: ReturnType<typeof setInterval> | null = null;
    if (reconnecting) {
      interval = setInterval(() => void fetchSnapshot(), pollIntervalMs);
    }
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [reconnecting, pollIntervalMs, replaceFromSnapshot]);

  return { isLoading, error };
}
